import { useRootStore } from 'stores/RootStore'
import { useEffect, useMemo } from 'react'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { LOW_CREDIT_THRESHOLD_RATIO } from 'components/Settings/Workspace/AutopilotPlans/utils'

export const useCreditUsage = () => {
  const { creditUsageStore, tierUsageStore } = useRootStore()
  const workspace = useCurrentWorkspace()
  const { currentSubscription } = tierUsageStore

  const { creditUsage, initialized, loading } = creditUsageStore

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      creditUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, creditUsageStore, workspace])

  const creditsRemaining = creditUsage?.credits_remaining ?? 0

  // Calculate credit status based on plan thresholds
  const creditStatus = useMemo(() => {
    if (!initialized || !currentSubscription) {
      return { isLowOnCredits: false, isOutOfCredits: false, status: 'normal' as const }
    }

    const isPioneerPlan = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
    const subscriptionCreditLimit = Number(currentSubscription.prices?.[0]?.metadata?.credit_limit ?? 0)
    const lowCreditsThreshold = subscriptionCreditLimit * LOW_CREDIT_THRESHOLD_RATIO

    let isOutOfCredits = creditsRemaining === 0
    let isLowOnCredits = creditsRemaining < lowCreditsThreshold && !isOutOfCredits
    let isNegativeCredits = creditsRemaining < 0

    // For Pioneer plans, don't show low or out of credits unless they have purchased additional credits
    if (isPioneerPlan && (creditUsage?.total_additional_credits || 0) === 0) {
      isOutOfCredits = false
      isLowOnCredits = false
    }

    let status: 'normal' | 'low' | 'out' | 'negative' = 'normal'

    if (isNegativeCredits) {
      status = 'negative'
    } else if (isOutOfCredits) {
      status = 'out'
    } else if (isLowOnCredits) {
      status = 'low'
    }

    return {
      isLowOnCredits,
      isOutOfCredits,
      isNegativeCredits,
      status,
    }
  }, [initialized, currentSubscription, creditsRemaining, creditUsage?.total_additional_credits])

  return {
    creditUsage,
    initialized,
    loading,
    contentCreditsUsed: creditUsage?.content_credits_used || 0,
    videoCreditsUsed: creditUsage?.video_credits_used || 0,
    adsCreditsUsed: creditUsage?.ads_credits_used || 0,
    totalCreditsAvailable: creditUsage?.total_credits_available || 0,
    totalCreditsUsed: creditUsage?.total_credits_used || 0,
    creditsRemaining,
    creditsExceeded: creditUsage?.credits_exceeded || false,
    subscriptionId: creditUsage?.subscription_id,
    startDate: creditUsage?.start_date,
    endDate: creditUsage?.end_date,
    usageCosts: creditUsage?.usage_costs,
    baseCreditsAvailable: creditUsage?.base_credits_available || 0,
    isLowOnCredits: creditStatus.isLowOnCredits,
    isOutOfCredits: creditStatus.isOutOfCredits,
    isNegativeCredits: creditStatus.isNegativeCredits,
    creditStatus: creditStatus.status,
    canAutoReload: creditUsage?.can_auto_reload || false,
    creditsExpiringSoon: creditUsage?.credits_expiring_soon ?? 0,
  }
}
